import moment from "moment"

import {ConditionType} from "../../../../../model2/conditions/condition-types"
import {RELATIVE_PERIOD_DEFAULT_VALUE} from "../../date-range-relative-period/date-range-relative-period"
import FieldsFactory from "../../../../fields-factory"
import FieldProto from "../../../../proto/field-proto"
import {FieldType} from "../../../../field-type"

// Single source of truth for a date/moment condition's value editing.
// Shared by FieldMoment_ConditionForm (automation / dashboard date filters) and by any
// composite condition that embeds a date sub-condition (e.g. task status-changed), so the
// condition-type -> value-field mapping and the per-type defaults can never drift apart.
//
// FieldsFactory is called lazily (inside the functions, never at module-eval time) because
// these helpers are pulled in through FieldsFactory itself - it is undefined while the
// circular import that loads the field modules is still resolving.

// Maps a date condition type to the field used to edit its value.
export function getMomentConditionValueField(conditionType?: ConditionType): FieldProto | null {
    switch (conditionType) {
        case ConditionType.DATE_RELATIVE_EQUALS:
        case ConditionType.DATE_RELATIVE_BEFORE:
        case ConditionType.DATE_RELATIVE_AFTER:
            return FieldsFactory.get({
                type    : FieldType.DATE_RELATIVE,
                fieldId : 'value'
            })

        case ConditionType.DATE_RELATIVE_RANGE:
            return FieldsFactory.get({
                type    : FieldType.DATE_RANGE_RELATIVE,
                fieldId : 'value'
            })

        case ConditionType.DATE_RELATIVE_PERIOD:
            return FieldsFactory.get({
                type    : FieldType.DATE_RANGE_RELATIVE_PERIOD,
                fieldId : 'value'
            })

        case ConditionType.DATE_FIXED_EQUALS:
        case ConditionType.DATE_FIXED_AFTER:
        case ConditionType.DATE_FIXED_BEFORE:
            return FieldsFactory.get({
                type        : FieldType.DATE,
                fieldId     : 'value',
                clearButton : false
            })

        case ConditionType.DATE_FIXED_RANGE:
            return FieldsFactory.get({
                type        : FieldType.DATE_RANGE,
                fieldId     : 'value',
                clearButton : false
            })

        default:
            return null
    }
}

// The default value applied for a given type.
// It MUST match what the value field renders for an empty value: those fields only DISPLAY
// their fallback (relative range -> today-today, relative period -> this week) and never emit
// it via onChange, so a value left undefined here reaches the backend as null while the UI
// shows a concrete range.
// Fixed range is the exception - its picker renders empty, so undefined is what the user sees.
export function defaultMomentConditionValue(conditionType?: ConditionType): any {
    switch (conditionType) {
        case ConditionType.DATE_RELATIVE_EQUALS:
        case ConditionType.DATE_RELATIVE_BEFORE:
        case ConditionType.DATE_RELATIVE_AFTER:
            return 0 // Today

        case ConditionType.DATE_RELATIVE_RANGE:
            return {start: 0, end: 0} // Today - Today

        case ConditionType.DATE_RELATIVE_PERIOD:
            return {...RELATIVE_PERIOD_DEFAULT_VALUE} // This week

        case ConditionType.DATE_FIXED_EQUALS:
        case ConditionType.DATE_FIXED_AFTER:
        case ConditionType.DATE_FIXED_BEFORE:
            return moment()

        default:
            return undefined
    }
}
